import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPost, apiDelete, authedFetch, chunkedUpload } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Upload, FileText, Image, CheckCircle2, XCircle, Clock,
         Plus, Trash2, Eye, BookOpen, Layers, Play, Library, UploadCloud, ImagePlus, Video, Layers3, ShieldCheck, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import ContentMgmtSyllabusBulk from './admin/ContentMgmtSyllabusBulk';
import ContentMgmtSyllabusLibrary from './admin/ContentMgmtSyllabusLibrary';
import { SchoolSyllabusIntakes } from './admin/SchoolSyllabusIntakes';
import { SchoolSuggestions } from './admin/SchoolSuggestions';
import { BookShelf } from './admin/BookShelf';
import { SchoolMappingTree } from './admin/SchoolMappingTree';
import LibraryIntegrationsPanel from '@/components/admin/LibraryIntegrationsPanel';
import AdminTasksButton from '@/components/admin/AdminTasksButton';
import QuestionBankPanel from './admin/QuestionBankPanel';
import ContentMgmtWorksheetsBulk from './admin/ContentMgmtWorksheetsBulk';
import ContentMgmtInfographicsBulk from './admin/ContentMgmtInfographicsBulk';
import ContentMgmtCinegraphicsBulk from './admin/ContentMgmtCinegraphicsBulk';
import ContentMgmtContentLibrary from './admin/ContentMgmtContentLibrary';
import ContentMgmtTreeLibrary from './admin/ContentMgmtTreeLibrary';
import ContentMgmtDataConsistency from './admin/ContentMgmtDataConsistency';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SupportStaffDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'upload');
  const routeLocation = useLocation();
  // A task notification can deep-link to a tab while we're already on this
  // page, so follow the URL rather than only reading it once on mount.
  useEffect(() => {
    const t = new URLSearchParams(routeLocation.search).get('tab');
    if (t) setTab(t);
  }, [routeLocation.search]);
  const [libraryRefreshToken, setLibraryRefreshToken] = useState(0);
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [contentItems, setContentItems] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  // Admin-only: delete a syllabus submission inline from Manage Content (useful
  // for clearing out legacy/pre-deployment rows without jumping to Syllabus Library).
  const deleteSubmissionInline = async (sub) => {
    if (!window.confirm(`Delete "${sub.subject}: ${sub.topic_name}"?\n\nRemoves:\n• the submission record\n• all RAG chunks\n• all rendered page images\n• the PDF on disk\n\nIrreversible.`)) return;
    try {
      await apiDelete(`/syllabus/submissions/${sub.submission_id}?delete_file=true`);
      toast.success('Deleted');
      setSubmissions(prev => prev.filter(x => x.submission_id !== sub.submission_id));
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Delete failed');
    }
  };

  // Upload form state
  const [form, setForm] = useState({ title: '', description: '', content_type: 'infographic', board: '', class_level: '', subject: '', topic: '', heading: '' });
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [contentHeadings, setContentHeadings] = useState([]);

  // Syllabus upload state
  const [syllabusForm, setSyllabusForm] = useState({ board: '', class_level: '', subject: '', topic_name: '' });
  const [syllabusFile, setSyllabusFile] = useState(null);
  const [contentsScreenshot, setContentsScreenshot] = useState(null);
  const [uploadingSyllabus, setUploadingSyllabus] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'support_staff') return;
    apiGet('/onboarding/options').then(d => setBoards(d.boards || [])).catch(() => {});
    apiGet('/content/library').then(d => setContentItems(d.items || [])).catch(() => {});
    apiGet('/syllabus/submissions').then(d => setSubmissions(d.submissions || [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (form.class_level && form.board) {
      const boardParam = `&board=${encodeURIComponent(form.board)}`;
      apiGet(`/onboarding/subjects?class_level=${form.class_level}${boardParam}`).then(d => setSubjects(d.subjects || [])).catch(() => {});
    }
  }, [form.class_level, form.board]);

  useEffect(() => {
    if (form.subject && form.class_level && form.board) {
      const boardParam = `&board=${encodeURIComponent(form.board)}`;
      apiGet(`/onboarding/topics?subject=${encodeURIComponent(form.subject)}&class_level=${form.class_level}${boardParam}`).then(d => setTopics(d.topics || [])).catch(() => {});
    }
  }, [form.subject, form.class_level, form.board]);

  // Load headings when chapter/topic changes (so staff can upload infographic per heading)
  useEffect(() => {
    if (form.topic && form.subject && form.board) {
      apiGet(`/education/headings?topic=${encodeURIComponent(form.topic)}&subject=${encodeURIComponent(form.subject)}&board=${encodeURIComponent(form.board)}&class_level=${form.class_level}`)
        .then(d => setContentHeadings(d.headings || []))
        .catch(() => setContentHeadings([]));
    } else {
      setContentHeadings([]);
    }
  }, [form.topic, form.subject, form.board, form.class_level]);

  const handleFileUpload = async (selectedFile) => {
    // Per-content-type ceilings (Phase 2.67):
    //   • cinegraphic (video) → 200 MB. Most of our HD chapter videos
    //     land in the 100-180 MB range — the previous 100 MB label was
    //     scaring staff off uploads that the chunked-upload backend has
    //     always been able to absorb.
    //   • infographic (image) → 25 MB. Images this big are almost
    //     certainly mistakes (someone uploaded a video as an "image").
    // The 2 GB hard ceiling on chunkedUpload still applies as a guardrail.
    const isVideo = form.content_type === 'cinegraphic';
    const maxBytes = isVideo ? 200 * 1024 * 1024 : 25 * 1024 * 1024;
    const maxLabel = isVideo ? '200 MB' : '25 MB';
    if (selectedFile && selectedFile.size > maxBytes) {
      const mb = (selectedFile.size / 1024 / 1024).toFixed(1);
      throw new Error(`File too large (${mb} MB). Max ${maxLabel} per ${isVideo ? 'video' : 'image'}.`);
    }
    // Final guardrail — chunked-upload backend caps at 2 GB regardless.
    if (selectedFile && selectedFile.size > 2 * 1024 * 1024 * 1024) {
      const gb = (selectedFile.size / 1024 / 1024 / 1024).toFixed(2);
      throw new Error(`File too large (${gb} GB). Max 2 GB per file.`);
    }
    try {
      // Returns shape { file_url, filename, stored_name, size, ... } — same
      // as the legacy single-POST so callers don't need to change.
      return await chunkedUpload(selectedFile);
    } catch (ex) {
      throw new Error(`Upload failed: ${ex?.message || 'unknown'}`);
    }
  };

  const [files, setFiles] = useState([]);   // multi-file picker — one content row per file
  const handleContentUpload = async () => {
    const list = files.length ? files : (file ? [file] : []);
    if (!form.title && list.length <= 1) { toast.error('Title is required'); return; }
    if (!list.length) { toast.error('Pick at least one file'); return; }
    setUploading(true);
    let ok = 0, fail = 0;
    const failures = [];
    for (const f of list) {
      try {
        const uploaded = await handleFileUpload(f);
        // For multi-file uploads, auto-derive a per-file title from the filename so
        // every row gets its own record without forcing the admin to retype.
        const perTitle = list.length > 1
          ? f.name.replace(/\.[^.]+$/, '')
          : form.title;
        await apiPost('/content/upload', { ...form, title: perTitle, file_url: uploaded.file_url, thumbnail_url: '' });
        ok += 1;
      } catch (e) {
        fail += 1;
        failures.push({ name: f.name, reason: e?.message || 'unknown error' });
      }
    }
    setUploading(false);
    if (fail === 0) {
      toast.success(`Uploaded ${ok} file${ok === 1 ? '' : 's'}`);
    } else {
      // Show ACTUAL reasons so staff knows what's wrong (file too large, etc.)
      const firstReason = failures[0]?.reason || 'unknown error';
      const extraNote = fail > 1 ? ` (+${fail - 1} other failure${fail > 2 ? 's' : ''})` : '';
      if (ok === 0) toast.error(`Upload failed: ${firstReason}${extraNote}`);
      else toast.warning(`${ok}/${ok + fail} uploaded. Failed: ${firstReason}${extraNote}`);
      // eslint-disable-next-line no-console
      console.error('Upload failures:', failures);
    }
    setForm({ ...form, title: '', description: '', heading: '' });
    setFile(null);
    setFiles([]);
    apiGet('/content/library').then(d => setContentItems(d.items || []));
  };

  const handleSyllabusUpload = async () => {
    if (!syllabusForm.board || !syllabusForm.class_level || !syllabusForm.subject || !syllabusForm.topic_name || !syllabusFile) {
      toast.error('All fields and PDF are required'); return;
    }
    setUploadingSyllabus(true);
    try {
      const pdfUploaded = await handleFileUpload(syllabusFile);
      let screenshotUrl = '';
      if (contentsScreenshot) {
        const ssUploaded = await handleFileUpload(contentsScreenshot);
        screenshotUrl = ssUploaded.file_url;
      }
      await apiPost('/syllabus/upload', { ...syllabusForm, pdf_url: pdfUploaded.file_url, contents_screenshot_url: screenshotUrl });
      toast.success('Syllabus submitted for review!');
      setSyllabusForm({ board: '', class_level: '', subject: '', topic_name: '' });
      setSyllabusFile(null);
      setContentsScreenshot(null);
      apiGet('/syllabus/submissions').then(d => setSubmissions(d.submissions || []));
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    setUploadingSyllabus(false);
  };

  if (user?.role !== 'admin' && user?.role !== 'support_staff') {
    return (
      <div className="max-w-2xl mx-auto px-4 pt-10 text-center">
        <Layers className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="font-heading text-xl font-bold text-slate-700 mb-2">Support Staff Dashboard</h2>
        <p className="text-sm text-slate-400 font-body">Access restricted to Support Staff and Admin roles.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 pt-6 pb-12" data-testid="support-dashboard">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Content Management</h1>
          <p className="text-sm text-slate-500 font-body">Upload infographics, cinegraphics, and syllabi</p>
        </div>
        <div className="ml-auto"><AdminTasksButton /></div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 rounded-xl bg-slate-100 p-1 w-fit flex-wrap">
        {[
          { key: 'upload', label: 'Upload Content', icon: Upload },
          { key: 'syllabus', label: 'Syllabus Upload', icon: FileText },
          { key: 'syllabus_bulk', label: 'Bulk Syllabus', icon: UploadCloud },
          { key: 'infographics_bulk', label: 'Bulk Infographics', icon: ImagePlus },
          { key: 'cinegraphics_bulk', label: 'Bulk Cinegraphics', icon: Video },
          { key: 'worksheets_bulk', label: 'Bulk Worksheets', icon: ClipboardCheck },
          { key: 'syllabus_library', label: 'Syllabus Library', icon: Library },
          { key: 'question_bank', label: 'Question Bank', icon: FileText },
          { key: 'content_library', label: 'Content Library', icon: Eye },
          { key: 'tree', label: 'Content Tree', icon: Layers3 },
          { key: 'school_mapping', label: 'School Mapping Tree', icon: Layers3 },
          { key: 'data_consistency', label: 'Data Consistency', icon: ShieldCheck },
          { key: 'manage', label: 'Manage Content', icon: Eye },
        ].map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-heading transition-all ${tab === t.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Upload Content Tab */}
      {tab === 'upload' && (
        <Card className="p-6 rounded-2xl border-slate-100" data-testid="upload-form">
          <h3 className="font-heading text-base font-semibold text-slate-800 mb-4">Upload Infographic / Cinegraphic</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Content Type</label>
              <select value={form.content_type} onChange={e => setForm({ ...form, content_type: e.target.value })}
                className="w-full p-2.5 rounded-xl border-2 border-slate-200 text-sm font-body">
                <option value="infographic">Infographic (Image)</option>
                <option value="cinegraphic">Cinegraphic (Video)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Title</label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Content title" className="rounded-xl" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Board</label>
              <select value={form.board} onChange={e => setForm({ ...form, board: e.target.value, subject: '', topic: '' })}
                className="w-full p-2.5 rounded-xl border-2 border-slate-200 text-sm font-body">
                <option value="">All Boards</option>
                {boards.map(b => <option key={b.board_id} value={b.name}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Class</label>
              <select value={form.class_level} onChange={e => setForm({ ...form, class_level: e.target.value, subject: '', topic: '' })}
                className="w-full p-2.5 rounded-xl border-2 border-slate-200 text-sm font-body">
                <option value="">All Classes</option>
                {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={String(i + 1)}>Class {i + 1}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Subject</label>
              <select value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value, topic: '' })}
                className="w-full p-2.5 rounded-xl border-2 border-slate-200 text-sm font-body">
                <option value="">Select Subject</option>
                {subjects.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Chapter/Topic</label>
              <select value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value, heading: '' })}
                className="w-full p-2.5 rounded-xl border-2 border-slate-200 text-sm font-body">
                <option value="">Select Topic</option>
                {topics.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            {contentHeadings.length > 0 && (
              <div>
                <label className="text-xs text-slate-500 font-body mb-1 block">Heading (optional)</label>
                <select data-testid="heading-select" value={form.heading} onChange={e => setForm({ ...form, heading: e.target.value })}
                  className="w-full p-2.5 rounded-xl border-2 border-slate-200 text-sm font-body">
                  <option value="">Whole Chapter</option>
                  {contentHeadings.map(h => <option key={h.heading_id || h.name} value={h.name}>{h.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="mb-4">
            <label className="text-xs text-slate-500 font-body mb-1 block">Description (optional)</label>
            <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description" className="rounded-xl" />
          </div>
          <div className="mb-4">
            <label className="text-xs text-slate-500 font-body mb-1 block">
              File{form.content_type === 'cinegraphic' ? 's' : ''}
              {form.content_type === 'cinegraphic' && <span className="text-slate-400 ml-1">(pick multiple — one row per video for the same chapter)</span>}
            </label>
            <input data-testid="content-file-input" type="file"
              multiple={form.content_type === 'cinegraphic'}
              onChange={e => {
                const arr = Array.from(e.target.files || []);
                setFiles(arr);
                setFile(arr[0] || null);
              }}
              accept={form.content_type === 'cinegraphic' ? 'video/*' : 'image/*'}
              className="w-full p-2 rounded-xl border-2 border-dashed border-slate-200 text-sm font-body" />
            {files.length > 1 && (
              <p className="text-[10px] text-slate-500 mt-1 font-body">
                {files.length} files picked · each will become a separate cinegraphic entry for the same chapter
              </p>
            )}
            <p className="text-[10px] text-slate-400 mt-1 font-body">
              {form.content_type === 'cinegraphic'
                ? 'Max 200 MB per video · MP4 / WebM recommended · large files chunk-upload automatically'
                : 'Max 25 MB per image · PNG / JPG / WebP'}
            </p>
          </div>
          <Button data-testid="upload-content-btn" onClick={handleContentUpload} disabled={uploading}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-full font-heading gap-1.5">
            <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload Content'}
          </Button>
        </Card>
      )}

      {/* Syllabus Upload Tab */}
      {tab === 'syllabus' && (
        <Card className="p-6 rounded-2xl border-slate-100" data-testid="syllabus-form">
          <h3 className="font-heading text-base font-semibold text-slate-800 mb-1">Upload Syllabus PDF</h3>
          <p className="text-xs text-slate-400 font-body mb-4">Upload syllabus for processing into the AI knowledge base</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Education Board</label>
              <select value={syllabusForm.board} onChange={e => setSyllabusForm({ ...syllabusForm, board: e.target.value })}
                className="w-full p-2.5 rounded-xl border-2 border-slate-200 text-sm font-body">
                <option value="">Select Board</option>
                {boards.map(b => <option key={b.board_id} value={b.name}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Class</label>
              <select value={syllabusForm.class_level} onChange={e => setSyllabusForm({ ...syllabusForm, class_level: e.target.value })}
                className="w-full p-2.5 rounded-xl border-2 border-slate-200 text-sm font-body">
                <option value="">Select Class</option>
                {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={String(i + 1)}>Class {i + 1}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Subject</label>
              <Input value={syllabusForm.subject} onChange={e => setSyllabusForm({ ...syllabusForm, subject: e.target.value })} placeholder="e.g. Physics" className="rounded-xl" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Topic Name (match book ToC)</label>
              <Input value={syllabusForm.topic_name} onChange={e => setSyllabusForm({ ...syllabusForm, topic_name: e.target.value })} placeholder="e.g. Electricity" className="rounded-xl" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Syllabus PDF</label>
              <input data-testid="syllabus-pdf-input" type="file" accept=".pdf" onChange={e => setSyllabusFile(e.target.files[0])}
                className="w-full p-2 rounded-xl border-2 border-dashed border-slate-200 text-sm font-body" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-body mb-1 block">Table of Contents Screenshot</label>
              <input data-testid="syllabus-screenshot-input" type="file" accept="image/*" onChange={e => setContentsScreenshot(e.target.files[0])}
                className="w-full p-2 rounded-xl border-2 border-dashed border-slate-200 text-sm font-body" />
            </div>
          </div>
          <Button data-testid="upload-syllabus-btn" onClick={handleSyllabusUpload} disabled={uploadingSyllabus}
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-heading gap-1.5">
            <FileText className="w-4 h-4" /> {uploadingSyllabus ? 'Uploading...' : 'Submit Syllabus'}
          </Button>
        </Card>
      )}

      {/* Bulk Syllabus Upload Tab — fresh component */}
      {tab === 'syllabus_bulk' && (
        <ContentMgmtSyllabusBulk onBatchIngested={() => setLibraryRefreshToken(t => t + 1)} />
      )}

      {/* Bulk Infographics Upload Tab */}
      {tab === 'infographics_bulk' && (
        <ContentMgmtInfographicsBulk onBatchIngested={() => setLibraryRefreshToken(t => t + 1)} />
      )}

      {/* Bulk Cinegraphics Upload Tab (Phase 2.68) */}
      {tab === 'cinegraphics_bulk' && (
        <ContentMgmtCinegraphicsBulk onBatchIngested={() => setLibraryRefreshToken(t => t + 1)} />
      )}

      {/* Syllabus Content Library Tab — approval workflow */}
      {tab === 'syllabus_library' && (
        <div className="space-y-6">
          <SchoolSyllabusIntakes />
          <BookShelf />
          <SchoolSuggestions />
          <ContentMgmtSyllabusLibrary refreshToken={libraryRefreshToken} />
          <LibraryIntegrationsPanel />
        </div>
      )}

      {/* Bulk Worksheets — admin-published practice sheets, per chapter */}
      {tab === 'worksheets_bulk' && <ContentMgmtWorksheetsBulk />}

      {/* Question Bank — student-uploaded past papers + the evidence they build */}
      {tab === 'question_bank' && <QuestionBankPanel />}

      {/* Content Library Tab — infographics + cinegraphics approval */}
      {tab === 'content_library' && (
        <ContentMgmtContentLibrary refreshToken={libraryRefreshToken} />
      )}

      {/* Content Tree Library — bird's-eye view of what's present vs missing */}
      {tab === 'tree' && (
        <div className="space-y-8"><SchoolMappingTree contentView /><ContentMgmtTreeLibrary refreshToken={libraryRefreshToken} /></div>
      )}
      {tab === 'school_mapping' && <SchoolMappingTree />}

      {/* Data Consistency — detect + normalize field-value drift (iter 55) */}
      {tab === 'data_consistency' && (
        <ContentMgmtDataConsistency />
      )}

      {/* Manage Content Tab */}
      {tab === 'manage' && (
        <div className="space-y-4" data-testid="manage-content">
          <h3 className="font-heading text-base font-semibold text-slate-800">Uploaded Content ({contentItems.length})</h3>
          {contentItems.length === 0 ? (
            <Card className="p-8 rounded-2xl border-slate-100 text-center">
              <p className="text-sm text-slate-400 font-body">No content uploaded yet.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {contentItems.map(item => (
                <Card key={item.content_id} className="p-4 rounded-xl border-slate-100" data-testid={`manage-item-${item.content_id}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.content_type === 'cinegraphic' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                      {item.content_type === 'cinegraphic' ? <Play className="w-5 h-5 text-purple-500" /> : <Image className="w-5 h-5 text-blue-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-heading font-semibold text-slate-700 truncate">{item.title}</p>
                      <p className="text-[10px] text-slate-400 font-body">{item.subject} · {item.topic} · {item.board || 'All'}</p>
                    </div>
                    {item.file_url && (
                      <a href={`${API_BASE}${item.file_url}`} target="_blank" rel="noopener noreferrer" data-testid={`preview-${item.content_id}`}
                        className="px-3 py-1.5 rounded-lg bg-slate-100 text-xs font-heading text-slate-600 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                        onClick={e => e.stopPropagation()}>
                        <Eye className="w-3.5 h-3.5 inline mr-1" />Preview
                      </a>
                    )}
                    <Badge className={item.content_type === 'cinegraphic' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}>
                      {item.content_type}
                    </Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Syllabus Submissions */}
          <h3 className="font-heading text-base font-semibold text-slate-800 mt-6">Syllabus Submissions ({submissions.length})</h3>
          {submissions.length === 0 ? (
            <Card className="p-6 rounded-2xl border-slate-100 text-center">
              <p className="text-sm text-slate-400 font-body">No syllabus submissions yet.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {submissions.map(s => (
                <Card key={s.submission_id} className="p-4 rounded-xl border-slate-100 flex items-center gap-4">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-heading font-medium text-slate-700">{s.subject}: {s.topic_name}</p>
                    <p className="text-[10px] text-slate-400 font-body">{s.board} · Class {s.class_level}</p>
                  </div>
                  <Badge className={s.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : s.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}>
                    {s.status === 'pending_verification' ? 'Pending' : s.status}
                  </Badge>
                  {user?.role === 'admin' && (
                    <button onClick={() => deleteSubmissionInline(s)}
                      data-testid={`manage-del-sub-${s.submission_id}`}
                      className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"
                      title="Delete this submission">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
